import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Save, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useSettings } from "@/hooks/useData";
import { useAuth } from "@/lib/auth";
import { TeamSection } from "@/components/TeamSection";
import { RemindersSection } from "@/components/Reminders";

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="overline block mb-2">{label}</span>
    {children}
  </label>
);

function ImageUpload({ label, value, onChange, kind, disabled, hint }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/files?kind=${kind}`, fd);
      onChange(data.id);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <span className="overline block mb-2">{label}</span>
      <div className="flex items-center gap-4">
        <div className="w-28 h-28 border border-border bg-white grid place-items-center overflow-hidden shrink-0">
          {value ? <img src={fileUrl(value)} alt={label} className="max-w-full max-h-full object-contain" crossOrigin="use-credentials" data-testid={`${kind}-preview`} /> : <span className="text-[10px] text-muted-foreground uppercase tracking-[0.2em]">None</span>}
        </div>
        <div className="space-y-2">
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`${kind}-file-input`} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          <button type="button" disabled={disabled || busy} onClick={() => ref.current.click()} data-testid={`${kind}-upload-button`} className="btn-press h-9 px-3 text-xs font-semibold border border-foreground hover:bg-foreground hover:text-background flex items-center gap-2 disabled:opacity-40">
            <Upload className="w-4 h-4" strokeWidth={1.5} /> {busy ? "Uploading…" : value ? "Replace" : "Upload"}
          </button>
          {value && !disabled && <button type="button" onClick={() => onChange("")} data-testid={`${kind}-remove-button`} className="h-9 px-3 text-xs font-semibold border border-border hover:border-destructive hover:text-destructive flex items-center gap-2"><X className="w-4 h-4" strokeWidth={1.5} /> Remove</button>}
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => { if (data && !s) setS(data); }, [data, s]);
  if (!s) return <div className="overline animate-pulse">Loading settings</div>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setPrefix = (k, v) => setS((p) => ({ ...p, prefixes: { ...p.prefixes, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put("/settings", { ...s, default_tax_rate: Number(s.default_tax_rate || 0) });
      qc.setQueryData(["settings"], saved);
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rise space-y-8" data-testid="settings-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="overline mb-3">{isAdmin ? "Admin" : "Workspace"}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Company settings</h1>
          {!isAdmin && <p className="text-sm text-muted-foreground mt-3" data-testid="settings-readonly-notice">You have read-only access. Only the workspace admin can change these settings.</p>}
        </div>
        {isAdmin && (
          <button onClick={save} disabled={saving} data-testid="admin-save-settings" className="btn-press h-11 px-6 bg-primary text-primary-foreground font-semibold text-sm flex items-center gap-2 hover:bg-[#C04E2D] disabled:opacity-50">
            <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save settings"}
          </button>
        )}
      </div>

      <fieldset disabled={!isAdmin} className="grid grid-cols-1 lg:grid-cols-2 gap-8 disabled:opacity-80">
        <section className="panel p-6 sm:p-8 space-y-5">
          <h2 className="font-display text-xl font-bold">Identity</h2>
          <ImageUpload label="Company logo" kind="logo" value={s.logo_file_id} onChange={(v) => set("logo_file_id", v)} disabled={!isAdmin} hint="PNG, JPG or WEBP up to 3 MB. Appears on every document." />
          <Field label="Company name"><input className="field" data-testid="settings-company-name" value={s.company_name} onChange={(e) => set("company_name", e.target.value)} /></Field>
          <Field label="Tagline"><input className="field" data-testid="settings-tagline" value={s.tagline} onChange={(e) => set("tagline", e.target.value)} /></Field>
          <Field label="Address"><textarea className="field-area" data-testid="settings-address" value={s.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email"><input className="field" data-testid="settings-email" value={s.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Phone"><input className="field" data-testid="settings-phone" value={s.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Website"><input className="field" data-testid="settings-website" value={s.website} onChange={(e) => set("website", e.target.value)} /></Field>
            <Field label="Tax / registration ID"><input className="field font-mono" data-testid="settings-tax-id" value={s.tax_id} onChange={(e) => set("tax_id", e.target.value)} /></Field>
          </div>
        </section>

        <div className="space-y-8">
          <section className="panel p-6 sm:p-8 space-y-5">
            <h2 className="font-display text-xl font-bold">Currency & tax</h2>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Currency code"><input className="field font-mono uppercase" data-testid="settings-currency-code" value={s.currency_code} onChange={(e) => set("currency_code", e.target.value.toUpperCase())} placeholder="USD" /></Field>
              <Field label="Symbol"><input className="field font-mono" data-testid="settings-currency-symbol" value={s.currency_symbol} onChange={(e) => set("currency_symbol", e.target.value)} placeholder="$" /></Field>
              <Field label="Default rate %"><input type="number" step="0.01" min="0" className="field font-mono" data-testid="settings-default-tax-rate" value={s.default_tax_rate} onChange={(e) => set("default_tax_rate", e.target.value)} /></Field>
            </div>
            <Field label="Tax label"><input className="field" data-testid="settings-tax-label" value={s.tax_label} onChange={(e) => set("tax_label", e.target.value)} placeholder="VAT, GST, Sales Tax…" /></Field>
          </section>

          <section className="panel p-6 sm:p-8 space-y-5">
            <h2 className="font-display text-xl font-bold">Numbering</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[["invoice", "Invoice"], ["quotation", "Quotation"], ["receipt", "Receipt"], ["voucher", "Voucher"]].map(([k, l]) => (
                <Field key={k} label={l}><input className="field font-mono uppercase" data-testid={`settings-prefix-${k}`} value={s.prefixes[k]} onChange={(e) => setPrefix(k, e.target.value.toUpperCase())} /></Field>
              ))}
            </div>
            <p className="text-xs text-muted-foreground font-mono">Format: PREFIX-YYYY-0001</p>
          </section>

          <section className="panel p-6 sm:p-8 space-y-5">
            <h2 className="font-display text-xl font-bold">Signature & footer</h2>
            <ImageUpload label="Manual signature (scanned)" kind="manual_signature" value={s.manual_signature_file_id} onChange={(v) => set("manual_signature_file_id", v)} disabled={!isAdmin} hint="Upload a scan of your handwritten signature to stamp documents with one click." />
            <Field label="Bank / payment details"><textarea className="field-area font-mono" data-testid="settings-bank-details" value={s.bank_details} onChange={(e) => set("bank_details", e.target.value)} placeholder={"Bank name\nAccount number\nIBAN / SWIFT"} /></Field>
            <Field label="Footer note"><input className="field" data-testid="settings-footer-note" value={s.footer_note} onChange={(e) => set("footer_note", e.target.value)} /></Field>
            <Field label="Accent colour"><div className="flex items-center gap-3"><input type="color" className="h-11 w-14 border border-border bg-background" data-testid="settings-accent-color" value={s.accent_color} onChange={(e) => set("accent_color", e.target.value)} /><span className="font-mono text-sm">{s.accent_color}</span></div></Field>
          </section>
        </div>
      </fieldset>
      <RemindersSection s={s} set={set} isAdmin={isAdmin} />
      {isAdmin && <TeamSection />}
    </div>
  );
}
